import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import './../../styles/CaseStudies.css'

// Client logos — one per case study. Midnight is built by IOHK / IOG, so the
// IOHK mark anchors that row.
import midnightLogo from '../../assets/clientLogos/iohklogo.webp'
import ovoLogo from '../../assets/clientLogos/ovo_transparent_logo.png'
import pumaLogo from '../../assets/clientLogos/Puma-logo-black-Transparent-Background.png'
import daznLogo from '../../assets/clientLogos/DAZN_transparent_logo.png'
import gsgLogo from '../../assets/clientLogos/gsg_logo_transparent.png'
import vegaLogo from '../../assets/clientLogos/vega_transparent_logo.webp'

/**
 * Case Studies index — lists every case study we've published.
 * Add new entries to the array below and they'll appear automatically.
 */
const studies = [
  {
    slug: 'puma',
    logo: pumaLogo,
    logoAlt: 'Puma',
    invert: true, // Puma logo is black-on-transparent — invert for dark theme
    eyebrow: 'E-commerce · Mobile · QA · Marketing CMS',
    client: 'Puma',
    partner: 'Global launch — first-ever mobile app + web rebrand',
    title: 'First mobile app. Full rebrand. Global launch.',
    summary:
      "We led testing and test automation for Puma's biggest digital programme to date — a global e-commerce relaunch and the company's first-ever mobile app — across Europe, the UK, North America, South Asia and the Middle East. Supported the marketing team with Sanity Studio component development, validated payment flows across multiple processors and regions, and shipped a fully autonomous merge-to-production pipeline.",
    stats: [
      ['1st', "mobile app in Puma's history"],
      ['100,000+', 'test cases run / week'],
      ['~45 min', 'merge → production'],
    ],
  },
  {
    slug: 'dazn',
    logo: daznLogo,
    logoAlt: 'DAZN',
    eyebrow: 'Streaming · OTT · Multi-device QA',
    client: 'DAZN',
    partner: 'Sports streaming — global launch',
    title: '200 countries. Many devices. One launch night.',
    summary:
      "We led test automation for DAZN's December 2020 launch into more than 200 countries — covering Smart TVs, PlayStation, Xbox, MagentaTV, SkyQ, Amazon Fire and the wider device matrix, plus DRM, region-locks and microservices.",
    stats: [
      ['11 months', '2020 – 2021 engagement'],
      ['200+', 'countries at launch'],
      ['Multi-device', 'TVs, consoles, mobile, web'],
    ],
  },
  {
    slug: 'ovo',
    logo: ovoLogo,
    logoAlt: 'OVO Energy',
    eyebrow: 'Energy · QA · ETL · Snowflake',
    client: 'OVO Energy',
    partner: 'Greenfield green-energy portal',
    title: 'Greenfield portal. Real billing. Real data, validated.',
    summary:
      "We led QA, ETL and Snowflake data validation on a brand-new portal that lets OVO customers and customer-service agents manage solar and wind generation, exports, and complex green-tariff billing — including the Smart Export Guarantee.",
    stats: [
      ['Greenfield', 'portal built from zero'],
      ['Delivered', 'QA, ETL & data validation'],
      ['Snowflake', 'data & ETL validated'],
    ],
  },
  {
    slug: 'midnight',
    logo: midnightLogo,
    logoAlt: 'IOHK / Input Output Global (parent of Midnight)',
    eyebrow: 'Blockchain · QA · Compliance · ETL',
    client: 'Midnight',
    partner: 'Cardano · IOHK / IOG',
    title: 'QA, compliance & ETL from inception to mainnet.',
    summary:
      "We led and architected the entire quality, compliance and data programme for Midnight — Cardano's privacy-first sidechain — from greenfield design through the Glacier Drop distribution and into the federated mainnet, supporting the launch of the 24-billion-token NIGHT supply.",
    stats: [
      ['16 months', '3 Sept 2024 – Jan 2026'],
      ['24B', 'NIGHT supply launched'],
      ['8 chains', 'Glacier Drop reach'],
    ],
  },
  {
    slug: 'global-savings-group',
    logo: gsgLogo,
    logoAlt: 'Global Savings Group',
    eyebrow: 'Commerce content · QA restructure',
    client: 'Global Savings Group',
    partner: 'Now Atolls — coupon platform',
    title: 'Org-wide QA, rebuilt from the inside out.',
    summary:
      "We led the QA engineering function and ran the project that overhauled the entire testing estate — migrating off Java-based frameworks to TypeScript stacks, building UI and API automation from scratch, and embedding test management and traceability across the organisation.",
    stats: [
      ['Org-wide', 'QA restructure'],
      ['Java → TS', 'framework migration'],
      ['From scratch', 'UI & API automation'],
    ],
  },
  {
    slug: 'vega',
    logo: vegaLogo,
    logoAlt: 'Vega Protocol',
    eyebrow: 'DeFi · Blockchain · Web3 QA',
    client: 'Vega Protocol',
    partner: 'DeFi derivatives platform on Ethereum',
    title: 'On-chain markets, tested every block.',
    summary:
      "We led QA engineering at Vega Protocol — a Layer 1 DeFi derivatives platform — building automation across the UI, the smart contracts, and the wallet integrations real users rely on. Cypress + Synpress, Ganache, Capsule, MetaMask, Trust Wallet, all in one programme.",
    stats: [
      ['DeFi', 'derivatives on Ethereum'],
      ['Smart contracts', 'tested via Synpress'],
      ['Multi-wallet', 'MetaMask, Trust, Vega'],
    ],
  },
  {
    slug: 'instant-offices',
    // No logo asset yet — row renders without one (handled by the && check below)
    eyebrow: 'Multi-brand · QA · Cloud migration · CI/CD',
    client: 'Instant Offices',
    partner: 'Three brands · coworker.com · easyoffices',
    title: 'Three brands. One QA strategy. Cloud-migrated, end-to-end.',
    summary:
      "We rebuilt the entire QA and test automation programme for one of the world's largest flexible-workspace groups — covering Instant Offices, coworker.com and easyoffices. One scalable Playwright + TypeScript framework across all three brands, full CI/CD test integration on every build, plus engineering support across a Rackspace → AWS migration with disaster recovery.",
    stats: [
      ['3 brands', 'Instant Offices, coworker.com, easyoffices'],
      ['200+', 'E2E tests per brand'],
      ['Rackspace → AWS', 'cloud migration with DR'],
    ],
  },
]

const CaseStudies = () => (
  <>
    <SEO
      path="/case-studies"
      title="Case Studies"
      description="Selected case studies from QA Tech — including the Midnight (Cardano / IOHK) launch programme covering QA, compliance and ETL from inception to mainnet."
      keywords="QA Tech case studies, software testing case studies, blockchain QA case study, Midnight Cardano case study, QA consultancy portfolio, software engineering portfolio"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Case Studies — QA Tech',
        url: 'https://qatechltd.com/case-studies',
        description:
          'Selected case studies from QA Tech showing how we deliver quality, engineering and data programmes for ambitious clients.',
        hasPart: studies.map((s) => ({
          '@type': 'Article',
          headline: `${s.client} — ${s.title}`,
          url: `https://qatechltd.com/case-studies/${s.slug}`,
        })),
      }}
    />

    <div className="body cs-index-body">
      <header className="cs-index-hero">
        <div className="cs-index-hero__bg" aria-hidden="true">
          <span className="cs-hero__orb cs-hero__orb--a" />
          <span className="cs-hero__orb cs-hero__orb--b" />
          <span className="cs-hero__grid" />
        </div>
        <div className="cs-index-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span>Case Studies</span>
          </nav>
          <span className="eyebrow">Case studies</span>
          <h1 className="cs-index-hero__title">
            Selected work from <span className="cs-index-hero__accent">high-stakes launches.</span>
          </h1>
          <p className="cs-index-hero__lede">
            A look at the engagements we&rsquo;re proudest of. More to come as
            we&rsquo;re cleared to publish.
          </p>
        </div>
      </header>

      <main className="cs-index-main">
        <ul className="cs-index-list">
          {studies.map((s, i) => (
            <li key={s.slug} className="cs-index-row">
              <Link to={`/case-studies/${s.slug}`} className="cs-index-link">
                <div className="cs-index-row__num" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="cs-index-row__main">
                  <div className="cs-index-row__brand">
                    {s.logo && (
                      <img
                        src={s.logo}
                        alt={s.logoAlt || s.client}
                        className={`cs-index-row__logo${s.invert ? ' cs-index-row__logo--invert' : ''}`}
                        loading="lazy"
                      />
                    )}
                    <span className="cs-index-row__eyebrow">{s.eyebrow}</span>
                  </div>
                  <h2 className="cs-index-row__title">
                    <span className="cs-index-row__client">{s.client}</span>
                    <span className="cs-index-row__partner">{s.partner}</span>
                  </h2>
                  <p className="cs-index-row__pitch">{s.title}</p>
                  <p className="cs-index-row__summary">{s.summary}</p>
                  <div className="cs-index-row__stats">
                    {s.stats.map(([num, label]) => (
                      <div key={label} className="cs-index-row__stat">
                        <span className="cs-index-row__stat-num">{num}</span>
                        <span className="cs-index-row__stat-label">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="cs-index-row__cta" aria-hidden="true">
                  <span>Read</span>
                  <span className="cs-index-row__arrow">&rarr;</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>

        <section className="cs-index-more">
          <p>
            More case studies are on the way. If you&rsquo;d like to talk
            about your own project,{' '}
            <Link to="/contact">drop us a line</Link>.
          </p>
        </section>
      </main>
    </div>
  </>
)

export default CaseStudies
